import { useState } from "react";
import { Gift, Copy, Check, Loader2, Users, CheckCircle2, Clock, WifiOff, Share2, UserPlus, Mail, MessageCircle, Linkedin, Percent, History } from "lucide-react";
import PageHeader from "@/components/dashboard/PageHeader";
import Button from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { useApiData } from "@/hooks/useApiData";
import { toastError, toastSuccess } from "@/lib/toast";
import { getMyReferrals } from "@/api/referrals";
import { formatDate } from "@/lib/dates";

const REWARD_PERCENT = 10;

const STEPS = [
  { icon: Share2, title: "Share your link", body: "Send it to anyone — teammates, clients, your network." },
  { icon: UserPlus, title: "They sign up & subscribe", body: `They get ${REWARD_PERCENT}% off their first plan automatically.` },
  { icon: Percent, title: "You both save", body: `You get ${REWARD_PERCENT}% off your next order — see it below.` },
];

// NO fixture. This page used to fall back to an invented code, link and stats
// ("QA7K9M2X", "4 referred / 2 converted") whenever the backend was unreachable,
// rendered exactly like the real thing and — unlike every other page here — with
// no demo banner. A referral link is a credential the user goes on to share
// publicly; handing them a fabricated one loses them real credit.
const EMPTY_STATS = { total: 0, converted: 0, pending: 0 };

const fmt = (v) => formatDate(v);

function Stat({ icon: Icon, value, label }) {
  return (
    <div className="card flex items-center gap-3 p-4">
      <span className="grid h-10 w-10 place-items-center rounded-xl bg-brand-500/10 text-brand-600 dark:text-brand-300"><Icon size={18} /></span>
      <div><div className="text-xl font-bold text-slate-800 dark:text-white">{value}</div><div className="text-xs text-slate-400">{label}</div></div>
    </div>
  );
}

export default function Referral() {
  const { data, loading, demo, error, reload } = useApiData(getMyReferrals, null);
  const [copied, setCopied] = useState(false);
  const link = (data && data.link) || "";
  const code = (data && data.code) || "";
  const stats = (data && data.stats) || EMPTY_STATS;
  const referrals = (data && data.referrals) || [];
  const rewards = (data && data.rewards) || [];
  const pendingDiscount = data && data.pending_discount_percent;
  const unavailable = demo || !!error || (!loading && !link);

  const shareMessage = `Try AI-QA-Engine — QA automation that explains itself. Sign up with my link and we both get ${REWARD_PERCENT}% off: ${link}`;
  const shareLinks = [
    { label: "Email", icon: Mail, href: `mailto:?subject=${encodeURIComponent("Try AI-QA-Engine")}&body=${encodeURIComponent(shareMessage)}` },
    { label: "WhatsApp", icon: MessageCircle, href: `https://wa.me/?text=${encodeURIComponent(shareMessage)}` },
    { label: "LinkedIn", icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(link)}` },
  ];

  // `navigator.clipboard` is undefined on an insecure origin, so the optional
  // chain silently no-oped while the button still flipped to "Copied".
  async function copy() {
    try {
      if (!navigator.clipboard) throw new Error("Clipboard unavailable");
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toastSuccess("Copied to clipboard");
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      toastError("Couldn't copy automatically — select the link and copy it manually.");
    }
  }

  // Only offered where the browser actually has a native share sheet (mainly
  // mobile) — a "More" button that silently does nothing on desktop is worse
  // than not having one.
  const canNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";
  async function shareMore() {
    try {
      await navigator.share({ title: "AI-QA-Engine", text: shareMessage, url: link });
    } catch (e) {
      /* user canceled the native share sheet — not an error */
    }
  }

  return (
    <>
      <PageHeader title="Refer & Earn" subtitle="Share your link — when someone signs up, we credit you." />

      {unavailable && !loading && (
        <div className="mb-5 flex flex-wrap items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-2.5 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
          <WifiOff size={15} className="shrink-0" />
          <span className="flex-1">Couldn&apos;t load your referral link. Nothing below is your real code — don&apos;t share it.</span>
          <button type="button" onClick={reload}
            className="rounded-lg border border-amber-300 px-2.5 py-1 text-xs font-medium hover:bg-amber-100 dark:border-amber-500/30 dark:hover:bg-amber-500/20">
            Retry
          </button>
        </div>
      )}

      {loading ? <div className="flex justify-center py-16 text-slate-400"><Loader2 size={24} className="animate-spin" /></div> : unavailable ? null : (
        <div className="space-y-6">
          <div className="card p-6">
            <div className="flex items-center gap-2 text-slate-800 dark:text-white"><Gift size={18} className="text-brand-500" /><h3 className="text-base font-semibold">Your referral link</h3></div>
            <p className="mt-0.5 text-sm text-slate-400">Code <span className="font-mono font-semibold text-slate-600 dark:text-slate-300">{code}</span></p>
            <div className="mt-4 flex flex-col items-stretch gap-2 sm:flex-row">
              <code className="min-w-0 flex-1 truncate rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-100">{link}</code>
              <Button onClick={copy} className="shrink-0">{copied ? <Check size={16} /> : <Copy size={16} />} {copied ? "Copied" : "Copy"}</Button>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="text-xs text-slate-400">Share via</span>
              {shareLinks.map((s) => (
                <a key={s.label} href={s.href} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/5">
                  <s.icon size={13} /> {s.label}
                </a>
              ))}
              {canNativeShare && (
                <button type="button" onClick={shareMore}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/5">
                  <Share2 size={13} /> More
                </button>
              )}
            </div>
          </div>

          {/* grid-cols-1/xs:3 avoided crushing icon+number+label into ~105px
              columns on a phone (see history); the 4th tile follows the same
              rule, just widening the breakpoint where it goes to a single row. */}
          <div className="grid grid-cols-1 gap-3 xs:grid-cols-2 sm:grid-cols-4">
            <Stat icon={Users} value={stats.total} label="Total referred" />
            <Stat icon={CheckCircle2} value={stats.converted} label="Converted" />
            <Stat icon={Clock} value={stats.pending} label="Pending" />
            <Stat icon={Percent} value={pendingDiscount ? `${pendingDiscount}%` : "—"} label="Off your next order" />
          </div>

          <div className="card p-5">
            <h3 className="mb-3 text-base font-semibold text-slate-800 dark:text-white">How it works</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {STEPS.map((s, i) => (
                <div key={s.title} className="flex items-start gap-3">
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-brand-500/10 text-sm font-semibold text-brand-600 dark:text-brand-300">{i + 1}</span>
                  <div>
                    <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-800 dark:text-white"><s.icon size={14} className="text-brand-500" /> {s.title}</p>
                    <p className="mt-0.5 text-xs text-slate-400">{s.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="card p-5">
            <h3 className="mb-3 text-base font-semibold text-slate-800 dark:text-white">Your referrals</h3>
            {referrals.length === 0 ? <p className="py-6 text-center text-sm text-slate-400">No referrals yet — share your link to get started.</p>
              : <div className="divide-y divide-slate-100 dark:divide-white/5">
                  {/* Index-qualified: `r.email || r.created_at` collided for two
                      referrals with no email recorded in the same second, and
                      React resolves a duplicate key by dropping a row. */}
                  {referrals.map((r, i) => (
                    <div key={r.id || `${r.email || "anon"}-${r.created_at || ""}-${i}`}
                         className="flex flex-wrap items-center justify-between gap-2 py-2.5">
                      <span className="min-w-0 truncate text-sm text-slate-700 dark:text-slate-200">{r.email || "—"}</span>
                      <div className="flex shrink-0 items-center gap-3">
                        <span className="text-xs text-slate-400">{fmt(r.created_at)}</span>
                        {r.status === "converted" ? <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">converted</Badge> : <Badge className="bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-slate-400">signed up</Badge>}
                      </div>
                    </div>
                  ))}
                </div>}
          </div>

          <div className="card p-5">
            <div className="mb-3 flex items-center gap-2 text-slate-800 dark:text-white"><History size={16} className="text-brand-500" /><h3 className="text-base font-semibold">Reward history</h3></div>
            {rewards.length === 0 ? <p className="py-6 text-center text-sm text-slate-400">No rewards yet — they show up here once a referral converts.</p>
              : <div className="divide-y divide-slate-100 dark:divide-white/5">
                  {rewards.map((r) => (
                    <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5">
                      <span className="text-sm text-slate-700 dark:text-slate-200">
                        {r.percent_off}% off — {r.role === "referrer" ? "for referring someone" : "welcome discount"}
                      </span>
                      <div className="flex shrink-0 items-center gap-3">
                        <span className="text-xs text-slate-400">{fmt(r.applied_at || r.created_at)}</span>
                        {r.status === "applied" ? <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">applied</Badge> : <Badge className="bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-slate-400">pending</Badge>}
                      </div>
                    </div>
                  ))}
                </div>}
          </div>
        </div>
      )}
    </>
  );
}
